// The application catalog adapter — the single seam between the UI and "which applications exist".
//
// Today the config source is static (config/r3.js, config/peoria.js). It is designed to be swapped for the
// shared company/application registry API without touching components: routing, navigation, the Studio
// Applications screen, and capability notices all read ONLY the functions exported here, and every value
// they receive is an APPLICATION DEFINITION of the shape documented in applications/README.md
// (definitionVersion 1 — a PROPOSAL for the registry to adopt or adjust).
//
// Pure module: no React, no window. `env` is injected once when the catalog is built.
import { buildR3Company } from './config/r3';
import { buildPeoriaCompany } from './config/peoria';
import { buildKrapfCompany } from './config/krapf';

export const DEFINITION_VERSION = 1;

function freezeDeep(o) {
  Object.values(o).forEach((v) => { if (v && typeof v === 'object' && !Object.isFrozen(v)) freezeDeep(v); });
  return Object.freeze(o);
}

/** Build a catalog from company configs. Exported for tests; the app uses the default `catalog`. */
export function createCatalog(companies) {
  const byTenant = new Map();
  const byAppId = new Map();
  const byRoute = new Map();
  const keys = new Map();
  companies.forEach((company) => {
    const { runtimeKeys = {}, applications = [], ...meta } = company;
    const apps = applications.map((a) => freezeDeep({
      definitionVersion: DEFINITION_VERSION,
      tenantId: company.tenantId,
      companyName: company.displayName,
      branding: company.branding,
      listed: true,
      ...a,
    }));
    byTenant.set(company.tenantId, freezeDeep({ ...meta, applications: apps }));
    apps.forEach((app) => {
      byAppId.set(app.appId, app);
      [app.route, ...(app.aliases || [])].forEach((r) => byRoute.set(r.toLowerCase(), app));
      if (runtimeKeys[app.appId]) keys.set(app.appId, runtimeKeys[app.appId]);
    });
  });

  return {
    /** Every company with a catalog: [{ tenantId, displayName }]. */
    listCompanies: () => [...byTenant.values()].map((c) => ({ tenantId: c.tenantId, displayName: c.displayName })),
    /** The definitions shown on the Applications page (listed !== false), or []. */
    listedApplicationsForCompany: (tenantId) => {
      const c = byTenant.get(String(tenantId || '').trim().toLowerCase());
      return c ? c.applications.filter((a) => a.listed !== false) : [];
    },
    /**
     * COMPANY-STUDIO-1: the company whose Studio a set of company-login groups may use (e.g. r3_admin → R3 with
     * Applications + Integration), or null. Groups are admin-assigned; never a pattern.
     */
    companyStudioForGroups: (groups) => {
      for (const c of byTenant.values()) {
        const admin = (c.studio && c.studio.adminGroups) || [];
        if (admin.some((g) => groups.has(g))) {
          return { tenantId: c.tenantId, displayName: c.displayName, screens: [...(c.studio.screens || [])] };
        }
      }
      return null;
    },
    /** A company's definitions, or [] — never another company's (an unknown tenant is not defaulted). */
    applicationsForCompany: (tenantId) => {
      const c = byTenant.get(String(tenantId || '').trim().toLowerCase());
      return c ? c.applications : [];
    },
    listApplications: () => [...byAppId.values()],
    getApplication: (appId) => byAppId.get(appId) || null,
    /** The definition owning a canonical route or one of its legacy aliases. */
    applicationForRoute: (path) => byRoute.get(String(path || '').toLowerCase()) || null,
    /** Legacy alias -> canonical route, for routing. */
    aliases: () => {
      const out = {};
      byAppId.forEach((app) => (app.aliases || []).forEach((al) => { out[al.toLowerCase()] = app.route; }));
      return out;
    },
    /**
     * Whether a company's customer-facing flow app has a capability configured. Returns
     * { known: false } for a company with no catalog, so callers never claim anything about it.
     */
    companyCapability: (tenantId, capability) => {
      const apps = byTenant.get(String(tenantId || '').trim().toLowerCase())?.applications || [];
      const flowApps = apps.filter((a) => a.runtime && a.runtime.engine === 'ecap-flow');
      if (flowApps.length === 0) return { known: false };
      const enabledIn = flowApps.filter((a) => a.capabilities && a.capabilities[capability] === true);
      // Customer chats that do NOT write Studio records (e.g. the agents-service chat), so screens can say so.
      const notRecorded = apps.filter((a) => a.type === 'agent-chat' && !(a.records && a.records.studio));
      return { known: true, enabled: enabledIn.length > 0, apps: flowApps, enabledIn, notRecorded };
    },
    /** Routing-only accessor for a public runtime key. Never pass the result to a rendered screen. */
    runtimeKeyFor: (appId) => keys.get(appId) || '',
  };
}

export const catalog = createCatalog([buildR3Company(process.env), buildPeoriaCompany(), buildKrapfCompany(process.env)]);
